import { useEffect, useMemo, useState } from "react";
import {
  App as AntdApp,
  Badge,
  Button,
  Card,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import type { UserInfo } from "../../types/auth";
import { ADMIN_USER_TYPES } from "../../types/auth";
import type { TicketCategory, TicketListItem } from "../../types/ticket";
import { ticketService } from "../../services/api/ticketService";
import { usePageTitle } from "../../hooks";
import { PageHeader } from "../../components/common/PageHeader";
import { TicketCreateModal } from "./TicketCreateModal";

const STATUS_COLOR: Record<number, string> = {
  1: "blue",
  2: "green",
  3: "default",
};

export const TicketsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { message: notify } = AntdApp.useApp();
  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );
  // Coordinador (user_type=4): ve las incidencias reportadas por su
  // equipo. Necesita saber quién creó cada una — misma columna y
  // filtro que ve el admin. Auditor (5) solo ve las suyas propias, así
  // que el filtro no le aporta nada.
  const isManager = identity?.app_user?.user_type === 4;
  const showCreator = isAdmin || isManager;
  // Navigate to whichever ticket-detail route the current user is allowed
  // to hit. Auditors live under /auditor/incidences/, admins under
  // /admin/tickets/ (the AuditorRoute guard would bounce them to /).
  const detailPath = (id: number) =>
    isAdmin ? `/admin/tickets/${id}` : `/auditor/incidences/${id}`;

  // Filtros sincronizados con la URL — al abrir el detalle de un
  // ticket y volver (back del navegador o botón "Volver" del show),
  // la URL conserva ?q=, ?status=, ?category=, ?kind=, ?creator= y el
  // listado se restaura exactamente donde estaba. Antes se perdían al
  // volver y el admin tenía que re-aplicarlos a mano para seguir
  // procesando incidencias.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") ?? "";
  const statusFilter = searchParams.get("status")
    ? Number(searchParams.get("status"))
    : undefined;
  const categoryFilter = searchParams.get("category")
    ? Number(searchParams.get("category"))
    : undefined;
  const kindFilter = (searchParams.get("kind") as "audit" | "support" | null) ?? undefined;
  const creatorFilter = useMemo(
    () =>
      (searchParams.get("creator") ?? "")
        .split(",")
        .map((s) => Number(s))
        .filter((n) => Number.isFinite(n) && n > 0),
    [searchParams],
  );
  // Tanto Resueltas (status=2) como Cerradas (status=3) se ocultan
  // por defecto — el admin trabaja casi siempre sobre lo abierto.
  // Toggle en la toolbar las re-incluye, y usar el filtro de status
  // explícito ("Resuelta" o "Cerrada") también las muestra
  // (interpreta como intención deliberada). Antes solo se ocultaban
  // las Cerradas (status=3); las Resueltas acababan llenando el
  // listado y haciendo perder foco al triar.
  const includeResolved = searchParams.get("resolved") === "1";
  // Paginación persistida en URL — antes era estado interno de la
  // Table y al "Volver a la lista" desde el detalle de una incidencia
  // la tabla se renderizaba de nuevo en página 1 aunque el admin
  // estuviera triando desde la 3. Ahora `?page=N` acompaña al resto
  // de filtros en `state.from` y restaura la vista exacta.
  const page = Math.max(1, Number(searchParams.get("page")) || 1);
  const pageSize = Math.max(10, Number(searchParams.get("pageSize")) || 25);

  const updateParam = (
    key: string,
    value: string | number | undefined | null,
  ) => {
    setSearchParams(
      (prev) => {
        if (value === undefined || value === null || value === "") {
          prev.delete(key);
        } else {
          prev.set(key, String(value));
        }
        // Al cambiar un filtro resetea la paginación — si el admin
        // estaba en página 5 con N resultados y ahora el filtro deja
        // 3 resultados, mostrarle "página 5 vacía" sería confuso.
        // page / pageSize no se consideran filtros aquí.
        if (key !== "page" && key !== "pageSize") {
          prev.delete("page");
        }
        return prev;
      },
      { replace: true },
    );
  };

  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  usePageTitle(t("tickets.title"));

  const reload = async () => {
    setLoading(true);
    try {
      const data = await ticketService.listTickets({ pageSize: 100 });
      setTickets(data.data);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    void ticketService.listCategories().then(setCategories).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((tk) => {
      // Oculta Resueltas (2) y Cerradas (3) salvo que el toggle las
      // incluya o el admin filtre explícitamente por ese status.
      // Esto evita que el tablero se llene de incidencias ya tratadas
      // y pierda foco de lo pendiente de triar.
      const isResolvedOrClosed = tk.status === 2 || tk.status === 3;
      if (
        !includeResolved
        && statusFilter !== tk.status
        && isResolvedOrClosed
      ) {
        return false;
      }
      if (statusFilter && tk.status !== statusFilter) return false;
      if (categoryFilter && tk.category !== categoryFilter) return false;
      if (kindFilter && tk.category_kind !== kindFilter) return false;
      if (creatorFilter.length > 0) {
        if (tk.created_by == null || !creatorFilter.includes(tk.created_by)) {
          return false;
        }
      }
      if (q) {
        const hay = `${tk.code} ${tk.subject} ${tk.created_by_name ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, search, statusFilter, categoryFilter, kindFilter, creatorFilter, includeResolved]);

  // Creadores únicos presentes en los tickets cargados — pobla el
  // selector "Filtrar por creador" para admin/coord. Excluimos filas
  // sin created_by (histórico o tickets del sistema).
  const creatorOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const tk of tickets) {
      if (tk.created_by != null && tk.created_by_name) {
        map.set(tk.created_by, tk.created_by_name);
      }
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tickets]);

  // When the user changes the kind filter, also clear the
  // category filter — categories are scoped to a single kind, so
  // the previous category id likely no longer applies.
  useEffect(() => {
    updateParam("category", undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindFilter]);

  // Restrict the category dropdown to the categories of the active
  // kind. When no kind is selected, show all.
  const visibleCategories = useMemo(
    () =>
      kindFilter
        ? categories.filter((c) => c.kind === kindFilter)
        : categories,
    [categories, kindFilter],
  );

  // Stable string comparator that puts null/empty values last regardless of order.
  const strCmp = (a: string | null | undefined, b: string | null | undefined) => {
    const av = (a ?? "").toLowerCase();
    const bv = (b ?? "").toLowerCase();
    if (!av && bv) return 1;
    if (av && !bv) return -1;
    return av.localeCompare(bv);
  };
  const numCmp = (a: number | null | undefined, b: number | null | undefined) =>
    (a ?? 0) - (b ?? 0);
  const dateCmp = (a: string | null | undefined, b: string | null | undefined) => {
    const av = a ? dayjs(a).valueOf() : 0;
    const bv = b ? dayjs(b).valueOf() : 0;
    return av - bv;
  };

  const columns = [
    {
      title: t("tickets.code"),
      dataIndex: "code",
      key: "code",
      width: 110,
      sorter: (a: TicketListItem, b: TicketListItem) => strCmp(a.code, b.code),
      render: (v: string) => (
        <strong style={{ whiteSpace: "nowrap" }}>{v}</strong>
      ),
    },
    {
      title: t("tickets.subject"),
      dataIndex: "subject",
      key: "subject",
      width: 220,
      ellipsis: { showTitle: false } as any,
      sorter: (a: TicketListItem, b: TicketListItem) => strCmp(a.subject, b.subject),
      render: (v: string) => (
        <Tooltip title={v} placement="topLeft" mouseEnterDelay={0.25}>
          <span style={{ fontWeight: 500 }}>{v}</span>
        </Tooltip>
      ),
    },
    {
      title: t("tickets.kind", "Origen"),
      dataIndex: "category_kind",
      key: "category_kind",
      width: 100,
      sorter: (a: TicketListItem, b: TicketListItem) =>
        strCmp(a.category_kind, b.category_kind),
      render: (v: string | undefined) =>
        v === "audit" ? (
          <Tag color="orange">{t("tickets.kind_audit", "Auditoría")}</Tag>
        ) : v === "support" ? (
          <Tag color="blue">{t("tickets.kind_support", "Soporte")}</Tag>
        ) : null,
    },
    {
      title: t("tickets.category"),
      dataIndex: "category_name",
      key: "category_name",
      width: 160,
      ellipsis: { showTitle: false } as any,
      sorter: (a: TicketListItem, b: TicketListItem) =>
        strCmp(a.category_name, b.category_name),
      render: (v: string) => (
        // antd's `<Tag>` doesn't truncate its own contents — without
        // these flex/overflow rules, long category names overflow the
        // pill and visually break the row. The Tooltip surfaces the
        // full name on hover.
        <Tooltip title={v} placement="topLeft" mouseEnterDelay={0.25}>
          <Tag
            style={{
              maxWidth: "100%",
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              verticalAlign: "middle",
            }}
          >
            {v}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: t("tickets.status"),
      dataIndex: "status",
      key: "status",
      width: 90,
      sorter: (a: TicketListItem, b: TicketListItem) => numCmp(a.status, b.status),
      render: (v: number) => (
        <Tag color={STATUS_COLOR[v]}>{t(`tickets.status_${v}`)}</Tag>
      ),
    },
    ...(showCreator
      ? [
          {
            title: t("tickets.created_by"),
            dataIndex: "created_by_name",
            key: "created_by_name",
            width: 140,
            ellipsis: { showTitle: false } as any,
            sorter: (a: TicketListItem, b: TicketListItem) =>
              strCmp(a.created_by_name, b.created_by_name),
            render: (v: string | null) =>
              v ? (
                <Tooltip title={v} placement="topLeft" mouseEnterDelay={0.25}>
                  <span>{v}</span>
                </Tooltip>
              ) : (
                "—"
              ),
          },
        ]
      : []),
    {
      title: t("tickets.messages"),
      dataIndex: "message_count",
      key: "message_count",
      width: 90,
      align: "center" as const,
      sorter: (a: TicketListItem, b: TicketListItem) =>
        numCmp(a.message_count, b.message_count),
      render: (count: number, row: TicketListItem) => (
        <Badge
          count={row.unread_count || 0}
          size="small"
          offset={[8, -2]}
          color="#FF8C00"
        >
          <Tag>{count}</Tag>
        </Badge>
      ),
    },
    {
      title: t("tickets.last_activity"),
      dataIndex: "last_activity",
      key: "last_activity",
      width: 150,
      // Default sort key for the table — most recent activity on top.
      sorter: (a: TicketListItem, b: TicketListItem) =>
        dateCmp(a.last_activity, b.last_activity),
      defaultSortOrder: "descend" as const,
      render: (v: string) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<WarningOutlined />}
        accent="#FA8C16"
        title={t("tickets.title")}
        subtitle={isAdmin ? t("tickets.subtitle_admin") : t("tickets.subtitle")}
        extra={
          // Admins do support — they only respond. New tickets are opened by
          // auditors / managers from their own profile.
          !isAdmin && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              {t("tickets.create")}
            </Button>
          )
        }
      />

      <Space
        direction="horizontal"
        size="middle"
        style={{ width: "100%", flexWrap: "wrap", marginBottom: 12 }}
      >
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t("tickets.search")}
          value={search}
          onChange={(e) => updateParam("q", e.target.value)}
          style={{ minWidth: 280 }}
        />
        <Select
          allowClear
          placeholder={t("tickets.filter_kind", "Tipo (Auditoría / Soporte)")}
          value={kindFilter}
          onChange={(v) => updateParam("kind", v)}
          options={[
            { value: "audit", label: t("tickets.kind_audit", "Auditoría") },
            { value: "support", label: t("tickets.kind_support", "Soporte") },
          ]}
          style={{ minWidth: 200 }}
        />
        <Select
          allowClear
          placeholder={t("tickets.filter_status")}
          value={statusFilter}
          onChange={(v) => updateParam("status", v)}
          options={[1, 2, 3].map((s) => ({ value: s, label: t(`tickets.status_${s}`) }))}
          style={{ minWidth: 160 }}
        />
        <Select
          allowClear
          placeholder={t("tickets.filter_category")}
          value={categoryFilter}
          onChange={(v) => updateParam("category", v)}
          options={visibleCategories.map((c) => ({ value: c.id, label: c.name }))}
          style={{ minWidth: 200 }}
        />
        {showCreator && (
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("tickets.filter_created_by", "Filtrar por creador")}
            value={creatorFilter}
            onChange={(v: number[]) =>
              updateParam("creator", v.length > 0 ? v.join(",") : undefined)
            }
            options={creatorOptions}
            style={{ minWidth: 220, maxWidth: 360 }}
            maxTagCount="responsive"
          />
        )}
        <Space size={6} align="center">
          <Switch
            size="small"
            checked={includeResolved}
            onChange={(checked) =>
              updateParam("resolved", checked ? "1" : undefined)
            }
          />
          <span style={{ color: "#595959", fontSize: 13 }}>
            {t("tickets.include_resolved", "Incluir resueltas y cerradas")}
          </span>
        </Space>
      </Space>

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          onChange: (nextPage, nextSize) => {
            setSearchParams(
              (prev) => {
                if (nextPage > 1) prev.set("page", String(nextPage));
                else prev.delete("page");
                if (nextSize !== 25) prev.set("pageSize", String(nextSize));
                else prev.delete("pageSize");
                return prev;
              },
              { replace: true },
            );
          },
        }}
        locale={{ emptyText: t("tickets.no_tickets") }}
        // Sum of fixed widths (~890) + a min for the elastic subject
        // column. Triggers horizontal scroll inside the Card when the
        // viewport is narrower than that, so the table never overflows
        // its container.
        scroll={{ x: 1080 }}
        // Clicking anywhere on the row navigates to the detail. The
        // cursor hint + hover background highlight the affordance.
        onRow={(row) => ({
          // Pasamos la URL del list (pathname + search) para que el
          // show pueda volver con los filtros intactos vía el botón
          // "Volver" (ver TicketShow). El back del navegador también
          // funciona porque los filtros ya viven en la URL vía
          // useSearchParams.
          onClick: () =>
            navigate(detailPath(row.id), {
              state: { from: location.pathname + location.search },
            }),
          style: { cursor: "pointer" },
        })}
        rowClassName={(row) => (row.unread_count > 0 ? "ticket-unread-row" : "")}
      />

      <style>{`
        .ticket-unread-row td { background-color: #fff7e6 !important; }
        .ant-table-tbody > tr:hover > td { background-color: #fafafa !important; }
      `}</style>

      <TicketCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        categories={categories}
        onCreated={async (ticket) => {
          notify.success(t("tickets.create_success"));
          setCreateOpen(false);
          await reload();
          navigate(detailPath(ticket.id));
        }}
        onError={() => notify.error(t("tickets.create_error"))}
      />
    </Card>
  );
};

export default TicketsList;
